export const formatBillingDate = (iso, language) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(language === "en" ? "en-GB" : "el-GR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const getBillingStatusColor = (status) => {
  if (status === "trialing") return "info";
  if (status === "active") return "success";
  if (["past_due", "unpaid", "incomplete", "paused"].includes(status)) {
    return "warning";
  }
  if (["canceled", "incomplete_expired"].includes(status)) return "error";
  return "default";
};

export const getBillingAccessColor = (billingAccessMode) => {
  if (billingAccessMode === "full") return "success";
  if (billingAccessMode === "read_only") return "warning";
  return "error";
};

export const getBillingPresentation = (billing, language, now = Date.now()) => {
  if (!billing) {
    return {
      severity: "info",
      messageKey: "billing_state_loading",
      dateFields: [],
    };
  }

  if (billing.canStartLocalTrial) {
    return { severity: "info", titleKey: "billing_start_free_trial", messageKey: "billing_trial_without_details", dateFields: [] };
  }

  // Access is always supplied by Core. Dates only explain an existing restriction.
  if (["read_only", "inactive"].includes(billing.billingAccessMode)) {
    const inactive = billing.billingAccessMode === "inactive";
    let messageKey = inactive ? "billing_state_inactive" : "billing_state_read_only";
    let dateFields = [];
    if (["past_due", "unpaid", "incomplete"].includes(billing.status)) {
      messageKey = inactive ? "billing_state_past_due_inactive" : "billing_state_past_due_read_only";
      dateFields = ["pastDueSince"];
    } else if (["trialing", "paused"].includes(billing.status) &&
        Date.parse(billing.trialEnd) <= now) {
      messageKey = "billing_state_trial_ended";
      dateFields = ["trialEnd"];
    } else if (billing.status === "paused" && !inactive) {
      messageKey = "billing_state_paused";
    }
    return {
      severity: inactive ? "error" : "warning",
      titleKey: inactive ? "billing_notice_restore_access" : "billing_notice_view_only",
      messageKey,
      dateFields,
    };
  }

  if (billing.checkoutOpensAt) {
    const messageDate = Number.isNaN(new Date(billing.checkoutOpensAt).getTime())
      ? null
      : formatBillingDate(billing.checkoutOpensAt, language);
    return {
      severity: "info",
      messageKey: messageDate
        ? "billing_state_checkout_not_open_until"
        : "billing_state_checkout_not_open",
      messageDate,
      dateFields: [],
    };
  }

  if (billing.freeAccessUntil && billing.nextAction === "checkout") {
    return {
      severity: "info",
      messageKey: "billing_state_early_signup",
      dateFields: ["freeAccessUntil"],
    };
  }

  if (billing.trialStartedLocally && billing.status === "trialing") {
    return { severity: "info", titleKey: "billing_notice_trial_active", messageKey: "billing_local_trial_active", dateFields: ["trialEnd"] };
  }

  if (billing.billingState !== "subscribed") {
    return {
      severity: "info",
      titleKey: "billing_notice_subscription",
      messageKey: billing.trialEligible === true
        ? "billing_state_ready_to_start"
        : billing.billingAccessMode === "full"
        ? "billing_state_full_without_subscription"
        : "billing_state_no_subscription",
      dateFields: [],
    };
  }

  if (
    billing.cancelAtPeriodEnd &&
    ["active", "trialing"].includes(billing.status)
  ) {
    return {
      severity: "warning",
      titleKey: "billing_notice_cancel_scheduled",
      messageKey: "billing_state_cancel_scheduled",
      dateFields: ["currentPeriodEnd"],
    };
  }

  if (billing.status === "trialing" && billing.billingAccessMode === "full") {
    return {
      severity: "info",
      titleKey: "billing_notice_trial_active",
      messageKey: billing.hasPaymentMethod === false
        ? "billing_state_trialing_without_card"
        : billing.hasPaymentMethod === true
        ? "billing_state_trialing_with_card"
        : "billing_state_trialing",
      dateFields: ["trialEnd"],
    };
  }

  if (billing.status === "past_due" && billing.billingAccessMode === "full") {
    return {
      severity: "warning",
      titleKey: "billing_notice_payment_attention",
      messageKey: "billing_state_past_due_grace",
      dateFields: ["pastDueSince"],
    };
  }

  if (billing.status !== "active") {
    return {
      severity: "info",
      titleKey: "billing_notice_subscription",
      messageKey: billing.billingAccessMode === "full"
        ? "billing_state_full_needs_review"
        : "billing_state_needs_review",
      dateFields: [],
    };
  }

  return {
    severity: "success",
    messageKey: "billing_state_active",
    dateFields: ["currentPeriodEnd"],
  };
};

export const getBillingRecoveryGuidance = (billing) => {
  if (!["read_only", "inactive"].includes(billing.billingAccessMode)) return null;
  if (billing.canManageBilling !== true) return "billing_contact_administrator";
  if (billing.checkoutOpensAt) return "billing_state_checkout_not_open";
  if (billing.nextAction === "checkout") {
    return billing.trialEligible === true
      ? "billing_recovery_start_trial"
      : "billing_recovery_start_subscription";
  }
  return billing.nextAction === "portal"
    ? "billing_recovery_manage_subscription"
    : "billing_recovery_contact_support";
};


export const billingActionLabel = billing => {
  if (billing.canStartLocalTrial) return "billing_start_free_trial";
  if (["past_due", "unpaid", "incomplete"].includes(billing.status) && billing.nextAction === "portal") return "billing_resolve_payment";
  if (billing.status === "trialing" && billing.billingAccessMode === "full" && !billing.cancelAtPeriodEnd &&
      (billing.trialStartedLocally || billing.hasPaymentMethod === false)) return "billing_add_payment_method";
  if (billing.nextAction === "checkout") return billing.trialEligible === true && !billing.trialStartedLocally
    ? "billing_start_free_trial" : "start_subscription";
  if (billing.nextAction === "portal" || billing.billingState === "subscribed") return "open_billing_portal";
  return "start_subscription";
};
